/**
 * The rules in `README.md`, parsed rather than restated.
 *
 * There is one source of record for the design rules and it is the README in
 * this package. The MCP server reads it in Python (`mcp/lopie_mcp/docs.py`)
 * and this reads it in JavaScript, and both parse the *same file* — so a rule
 * added once appears in `design_rules` and on the console's Design screen
 * with no change to either.
 *
 * Two parsers, one document. That is deliberate and it is the cheap half of
 * the trade: a shared JSON sidecar would be one parser and two documents, and
 * the sidecar is the copy that goes stale. The thing that must not fork is
 * the prose, and it does not.
 *
 * ## Why the enforcement label lives in the prose
 *
 * Because a second list drifts, and this particular list drifting would be
 * the worst kind: a rule quietly labelled "enforced" that nothing enforces.
 * Keeping `**Enforced by** ...` on the line under each heading means the
 * label is edited in the same breath as the rule it describes, is reviewed in
 * the same diff, and is legible to somebody reading the README with no tools
 * at all — which the README's own audience mostly is.
 *
 * This module imports nothing. It is given markdown and returns objects.
 */

/** The three categories, and what each one actually claims.
 *
 * The definitions are here rather than in the screen that renders them
 * because the MCP's caller needs them too, and an agent told a rule is
 * "review" without being told what review means has been told a colour. */
export const CATEGORIES = {
  kernel: {
    id: "kernel",
    label: "Kernel refuses it",
    short: "kernel",
    detail:
      "The kernel refuses it at runtime. Breaking the rule does not produce " +
      "an ugly screen; it produces a 403, or a ceiling that is not yours. " +
      "You cannot ship past it.",
    // Semantic, not brand. `permitted` is the strongest state this palette
    // has and this is the strongest guarantee the list has.
    colour: "kernel.permitted",
  },
  test: {
    id: "test",
    label: "A test fails",
    short: "test",
    detail:
      "A suite in this repo goes red. It catches the specific, mechanical " +
      "half of a rule and never the judgement half.",
    colour: "kernel.unattended",
  },
  review: {
    id: "review",
    label: "A reviewer, or nobody",
    short: "review",
    detail:
      "Prose. A human has to notice. Nothing runs, and nothing will tell " +
      "you afterwards.",
    colour: "kernel.attention",
  },
};

export const ORDER = ["kernel", "test", "review"];

const HEADING = /^(#{1,6})\s+(.*?)\s*#*$/;

/**
 * `**Enforced by** `kernel` · `review` — sentence`, wrapped over any number
 * of lines, ending at the first blank line.
 *
 * Strict on the lead-in and loose on everything after it. A rule whose label
 * this cannot read comes back with an empty `by` and a null sentence, which
 * the callers render as *unlabelled* — never as `review`, and never as
 * nothing. Guessing "probably review" would be inventing the one field this
 * whole exercise exists to stop anybody inventing.
 */
const ENFORCED = /^\*\*Enforced by\*\*\s+(?<labels>.+?)\s+[—–-]\s+(?<detail>.*)$/;
const LABEL = /`([a-z]+)`/g;

export function parseEnforcement(body) {
  const lines = String(body).split(/\r?\n/);
  const start = lines.findIndex((line) => line.trimStart().startsWith("**Enforced by**"));
  if (start === -1) return { by: [], detail: null, unlabelled: true };

  // Fold the wrapped continuation, the way the README actually writes it.
  let joined = lines[start].trim();
  for (let i = start + 1; i < lines.length; i += 1) {
    if (!lines[i].trim()) break;
    joined = `${joined} ${lines[i].trim()}`;
  }

  const match = ENFORCED.exec(joined);
  if (!match) return { by: [], detail: null, unlabelled: true };

  const by = [];
  for (const [, id] of match.groups.labels.matchAll(LABEL)) {
    if (CATEGORIES[id] && !by.includes(id)) by.push(id);
  }
  by.sort((a, b) => ORDER.indexOf(a) - ORDER.indexOf(b));

  return {
    by,
    // Unknown labels are kept rather than dropped, so a category added to the
    // README before it is added here is visible instead of silently absent.
    unknown: [...match.groups.labels.matchAll(LABEL)]
      .map(([, id]) => id)
      .filter((id) => !CATEGORIES[id]),
    detail: plain(match.groups.detail),
    markdown: joined,
    unlabelled: by.length === 0,
  };
}

/**
 * Every `###` under `## The rules`, with its body, its enforcement label and
 * the reasoning that was already there.
 *
 * `why` keeps the enforcement line in it. Nothing is dropped by this parser:
 * a caller that wants the prose without the label has `reasoning`, and a
 * caller that wants everything has `markdown`. A parser that silently ate a
 * paragraph would be worse than no parser — the same rule `docs.py` states.
 */
export function parseRules(markdown) {
  const lines = String(markdown).split(/\r?\n/);
  const found = [];
  lines.forEach((line, index) => {
    const match = HEADING.exec(line);
    if (match) found.push({ level: match[1].length, title: match[2].trim(), index });
  });

  const out = [];
  let parent = null;
  for (let i = 0; i < found.length; i += 1) {
    const heading = found[i];
    if (parent === null) {
      if (heading.level === 2 && slug(heading.title) === "the rules") parent = heading.level;
      continue;
    }
    if (heading.level <= parent) break;

    const end = i + 1 < found.length ? found[i + 1].index : lines.length;
    const body = lines.slice(heading.index + 1, end).join("\n").trim();
    const enforcement = parseEnforcement(body);
    out.push({
      rule: heading.title,
      id: slug(heading.title).replace(/\s+/g, "-"),
      why: body,
      // The prose that was already there, with the label paragraph lifted
      // out — so a screen can render the label as a label and the reasoning
      // as reasoning, without either being retyped.
      reasoning: withoutLabel(body),
      enforcement,
      markdown: `${"#".repeat(heading.level)} ${heading.title}\n\n${body}\n`,
    });
  }

  return out;
}

/** The prose under a rule with the enforcement paragraph removed. */
function withoutLabel(body) {
  const lines = body.split(/\r?\n/);
  const start = lines.findIndex((line) => line.trimStart().startsWith("**Enforced by**"));
  if (start === -1) return body;
  let end = start;
  while (end < lines.length && lines[end].trim()) end += 1;
  return [...lines.slice(0, start), ...lines.slice(end)].join("\n").trim();
}

/**
 * How many rules each category actually covers.
 *
 * Returned as counts rather than a sentence so the screen can say the
 * uncomfortable number out loud: `reviewOnly` is the one that matters, and it
 * is the biggest.
 */
export function summarise(rules) {
  const count = (id) => rules.filter((r) => r.enforcement.by.includes(id)).length;
  return {
    total: rules.length,
    kernel: count("kernel"),
    test: count("test"),
    review: count("review"),
    reviewOnly: rules.filter(
      (r) => r.enforcement.by.length === 1 && r.enforcement.by[0] === "review",
    ).length,
    unlabelled: rules.filter((r) => r.enforcement.unlabelled).length,
  };
}

function slug(title) {
  return title.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

/** Markdown emphasis stripped, so a caller gets a sentence.
 *
 * The markdown is always returned beside this, so nothing is lost. Same
 * function, same reason, as `_plain` in `docs.py`. */
function plain(value) {
  return String(value)
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, "$1")
    .replace(/`/g, "")
    .replace(/\s+/g, " ")
    .trim();
}
